import { useEffect, useState } from 'react';
import { Navigate, Outlet, useLocation } from 'react-router-dom';
import { tokenStore } from '@/api/token-store';
import { PageLoader } from '@/shared/components/feedback/LoadingSpinner';
import { restoreSession, useAuthStore } from '../store/auth-store';

/**
 * Protects routes that require authentication.
 *
 * Session restoration is handled here (not in App.tsx) so that public routes
 * (landing page, demo) render immediately without waiting for session restoration.
 *
 * Flow:
 * 1. If no refresh token → sessionReady immediately (no delay)
 * 2. If refresh token exists → attempt silent restoration, then mark ready
 * 3. If not authenticated after restoration → redirect to /login
 */
export function AuthGuard() {
  const isAuthenticated = useAuthStore((s) => s.isAuthenticated);
  const location = useLocation();

  // If no refresh token, skip restoration entirely (no flash for new visitors)
  const [sessionReady, setSessionReady] = useState(() => !tokenStore.getRefreshToken());

  useEffect(() => {
    if (sessionReady) return;
    void restoreSession().finally(() => setSessionReady(true));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  if (!sessionReady) return <PageLoader />;

  if (!isAuthenticated) {
    return <Navigate to="/login" state={{ from: location }} replace />;
  }

  return <Outlet />;
}
